import type { MiniAppApi, VerifyError, VerifySuccess } from './api';
import { waitForTelegramSDK } from './telegram';
import { useSessionStore, type SessionSource } from './session-store';

const SESSION_STORAGE_KEY = 'lexy_webapp_session_v1';
const DEFAULT_SESSION_TTL_MS = 12 * 60 * 60 * 1000; // 12h fallback if expiresAt missing

export type AuthStatus =
  | 'loading'
  | 'authenticated'
  | 'cached-session'
  | 'read-only'
  | 'needs-initdata'
  | 'error';

type SessionOrigin = Exclude<SessionSource, 'none'>;

export interface TelegramAuthSession {
  userId?: number;
  telegramUserId?: number;
  sessionToken?: string;
  traceId?: string;
  source: SessionOrigin;
}

export interface TelegramAuthState {
  status: AuthStatus;
  session?: TelegramAuthSession;
  message?: string;
  error?: string;
}

export interface PersistedSession {
  userId: number;
  telegramUserId?: number;
  sessionToken: string;
  traceId?: string;
  source: SessionOrigin;
  storedAt: number;
  expiresAt?: string;
}

const runtimeWindow: any =
  typeof globalThis === 'object' && globalThis !== null && 'window' in globalThis
    ? (globalThis as any).window
    : undefined;
const runtimeNavigator: any =
  typeof globalThis === 'object' && globalThis !== null && 'navigator' in globalThis
    ? (globalThis as any).navigator
    : undefined;
const isBrowser = Boolean(runtimeWindow);

export function loadPersistedSession(): PersistedSession | null {
  if (!isBrowser) {
    return null;
  }
  try {
    const raw = runtimeWindow?.localStorage?.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!parsed.sessionToken || !parsed.userId) {
      runtimeWindow?.localStorage?.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    if (typeof parsed.telegramUserId !== 'number') {
      parsed.telegramUserId = parsed.userId;
    }
    const ttlTarget =
      parsed.expiresAt && !Number.isNaN(Date.parse(parsed.expiresAt))
        ? new Date(parsed.expiresAt).getTime()
        : parsed.storedAt + DEFAULT_SESSION_TTL_MS;
    if (Number.isFinite(ttlTarget) && Date.now() > ttlTarget) {
      runtimeWindow?.localStorage?.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn('[Auth] Failed to load persisted session:', error);
    return null;
  }
}

export function persistSession(session: PersistedSession): void {
  if (!isBrowser) {
    return;
  }
  try {
    runtimeWindow?.localStorage?.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    console.warn('[Auth] Failed to persist session:', error);
  }
}

export function clearPersistedSession(): void {
  if (!isBrowser) {
    return;
  }
  try {
    runtimeWindow?.localStorage?.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.warn('[Auth] Failed to clear persisted session:', error);
  }
}

interface InitDataSnapshot {
  raw: string;
  hasInitData: boolean;
  unsafeUserId?: number;
}

function extractUserIdFromInitData(raw: string): number | undefined {
  if (!raw) {
    return undefined;
  }
  try {
    const params = new URLSearchParams(raw);
    const userParam = params.get('user');
    if (!userParam) {
      return undefined;
    }
    const parsedUser = JSON.parse(userParam);
    const candidate = parsedUser?.telegramId ?? parsedUser?.id;
    return typeof candidate === 'number' ? candidate : undefined;
  } catch (error) {
    console.warn('[Auth] Failed to parse user id from initData:', error);
    return undefined;
  }
}

interface ResolvedIdentifiers {
  userId?: number;
  telegramUserId?: number;
}

function resolveUserIdentifiers(
  snapshot: InitDataSnapshot,
  verifyResult?: VerifySuccess
): ResolvedIdentifiers {
  const telegramCandidates = [
    snapshot.unsafeUserId,
    verifyResult?.data?.user?.telegramId,
    extractUserIdFromInitData(snapshot.raw),
  ];

  const telegramUserId = telegramCandidates.find(
    (value): value is number => typeof value === 'number'
  );

  const userId = ((): number | undefined => {
    if (typeof verifyResult?.data?.user?.id === 'number') {
      return verifyResult.data.user.id;
    }
    return telegramUserId;
  })();

  return { userId, telegramUserId };
}

async function readInitDataSnapshot(): Promise<InitDataSnapshot> {
  // Log raw Telegram WebApp state BEFORE any processing
  const webApp = runtimeWindow?.Telegram?.WebApp;
  const rawInitData = webApp?.initData ?? '';
  const rawInitDataUnsafe = webApp?.initDataUnsafe;
  
  console.log('[InitDataSnapshot] Raw Telegram WebApp state:', {
    hasWebApp: !!webApp,
    initDataLength: rawInitData.length,
    initDataPreview: rawInitData.substring(0, 100),
    initDataUnsafe: rawInitDataUnsafe ? {
      hasUser: !!rawInitDataUnsafe.user,
      userId: rawInitDataUnsafe.user?.id,
      hasAuthDate: !!rawInitDataUnsafe.auth_date,
      authDate: rawInitDataUnsafe.auth_date,
    } : null,
  });
  
  // Use waitForInitData to get initData with fallback on themeChanged
  // Increased timeout to 10 seconds to allow Telegram SDK to fully initialize
  const { waitForInitData } = await import('./telegram');
  const raw = await waitForInitData(10000); // Wait up to 10 seconds
  
  // Log after waiting
  const finalInitData = runtimeWindow?.Telegram?.WebApp?.initData ?? '';
  const finalUnsafeUserId = runtimeWindow?.Telegram?.WebApp?.initDataUnsafe?.user?.id;
  
  console.log('[InitDataSnapshot] After waitForInitData:', {
    rawLength: raw.length,
    rawPreview: raw.substring(0, 100),
    finalInitDataLength: finalInitData.length,
    finalInitDataPreview: finalInitData.substring(0, 100),
    finalUnsafeUserId,
    hasInitData: raw.trim().length > 0,
  });
  
  // Use the longer of the two (raw from waitForInitData or current webApp.initData)
  const effectiveInitData = raw.trim().length > 0 ? raw : finalInitData;
  const hasInitData = effectiveInitData.trim().length > 0;
  
  return {
    raw: effectiveInitData,
    hasInitData,
    unsafeUserId: finalUnsafeUserId,
  };
}

function applySessionToStore(session: TelegramAuthSession): void {
  const setSession = useSessionStore.getState().setSession;
  setSession({
    sessionToken: session.sessionToken,
    traceId: session.traceId,
    userId: session.userId,
    telegramUserId: session.telegramUserId ?? session.userId,
    sessionSource: session.source,
  });
}

function clearSessionStore(): void {
  useSessionStore.getState().clear();
}

function buildReadOnlyState(
  telegramId: number,
  message: string,
  errorMessage?: string
): TelegramAuthState {
  const readOnlySession: TelegramAuthSession = {
    userId: telegramId,
    telegramUserId: telegramId,
    source: 'read-only',
  };
  applySessionToStore(readOnlySession);
  return {
    status: 'read-only',
    session: readOnlySession,
    message,
    error: errorMessage,
  };
}

function buildNeedsInitDataState(message: string, errorMessage?: string): TelegramAuthState {
  clearPersistedSession();
  clearSessionStore();
  return {
    status: 'needs-initdata',
    message,
    error: errorMessage,
  };
}

function mapVerificationErrorToState(
  verifyResult: VerifyError,
  snapshot: InitDataSnapshot
): TelegramAuthState {
  const code = verifyResult.error.code?.toUpperCase() ?? 'UNKNOWN';
  const hasUnsafeUserId = typeof snapshot.unsafeUserId === 'number';
  const isInitDataIssue =
    code.startsWith('INITDATA_') || code === 'INVALID_USER_DATA' || code === 'INVALID_INITDATA';

  if (isInitDataIssue) {
    console.warn('[Auth] InitData error', {
      errorCode: code,
      errorMessage: verifyResult.error.message,
      hasUnsafeUserId,
    });

    if (hasUnsafeUserId) {
      return buildReadOnlyState(
        snapshot.unsafeUserId!,
        'Срок действия данных Telegram истёк. Включён режим просмотра. Данные могут быть неактуальными.',
        verifyResult.error.message
      );
    }

    return buildNeedsInitDataState(
      'Ошибка авторизации. Откройте мини-приложение через кнопку в боте, чтобы повторно авторизоваться.',
      verifyResult.error.message
    );
  }

  const isRecoverableNetworkError =
    code === 'NETWORK_ERROR' ||
    code === 'TIMEOUT' ||
    code === 'ECONNABORTED' ||
    code === 'ECONNREFUSED';

  if (isRecoverableNetworkError && hasUnsafeUserId) {
    console.warn('[Auth] Recoverable error. Switching to read-only mode.', {
      errorCode: code,
      errorMessage: verifyResult.error.message,
      hasUnsafeUserId,
    });
    return buildReadOnlyState(
      snapshot.unsafeUserId!,
      'Сервер временно недоступен. Включён режим просмотра. Данные могут быть неактуальными.',
      verifyResult.error.message
    );
  }

  const isCriticalError =
    code === 'SERVER_ERROR' ||
    code === 'INTERNAL_ERROR' ||
    code === 'CONFIGURATION_ERROR';

  clearPersistedSession();
  clearSessionStore();
  
  console[isCriticalError ? 'error' : 'warn']('[Auth] Verification error', {
    errorCode: code,
    errorMessage: verifyResult.error.message,
    isCritical: isCriticalError,
  });
  
  const fallbackMessage = isCriticalError
    ? 'Ошибка сервера. Попробуйте позже или обратитесь в поддержку.'
    : isRecoverableNetworkError
    ? 'Не удалось подтвердить данные Telegram. Проверьте интернет-соединение и попробуйте снова.'
    : 'Не удалось подтвердить данные Telegram.';

  return {
    status: 'error' as const,
    error: verifyResult.error.message,
    message: fallbackMessage,
  };
}

function buildSessionFromPersisted(
  persisted: PersistedSession
): TelegramAuthSession {
  return {
    userId: persisted.userId,
    telegramUserId: persisted.telegramUserId ?? persisted.userId,
    sessionToken: persisted.sessionToken,
    traceId: persisted.traceId,
    source: persisted.source,
  };
}

function buildMetaPayload(): Record<string, unknown> {
  if (!isBrowser) {
    return {};
  }
  const webApp = runtimeWindow?.Telegram?.WebApp;
  return {
    userAgent: runtimeNavigator?.userAgent,
    platform: webApp?.platform,
    version: webApp?.version,
    themeParams: webApp?.themeParams,
  };
}

export async function authenticateWithTelegram(
  api: MiniAppApi
): Promise<TelegramAuthState> {
  const isTelegramWebApp = isBrowser && Boolean(runtimeWindow?.Telegram?.WebApp);

  if (!isTelegramWebApp) {
    const persisted = loadPersistedSession();
    if (persisted) {
      const session = buildSessionFromPersisted(persisted);
      applySessionToStore({ ...session, source: 'cached' });
      return {
        status: 'cached-session',
        session: { ...session, source: 'cached' },
        message: 'Используем сохранённую сессию',
      };
    }
    clearSessionStore();
    return {
      status: 'read-only',
      message:
        'Откройте мини-приложение внутри Telegram, чтобы авторизоваться и загрузить данные.',
    };
  }

  await waitForTelegramSDK();
  const snapshot = await readInitDataSnapshot();

  console.log('[Auth] InitData snapshot result:', {
    hasInitData: snapshot.hasInitData,
    rawLength: snapshot.raw.length,
    rawPreview: snapshot.raw.substring(0, 100),
    unsafeUserId: snapshot.unsafeUserId,
  });

  if (!snapshot.hasInitData) {
    // Check for persisted session first
    const persisted = loadPersistedSession();
    if (persisted) {
      const session = buildSessionFromPersisted(persisted);
      applySessionToStore({ ...session, source: 'cached' });
      console.log('[Auth] Cached session restored', {
        userId: session.userId,
        source: 'persisted',
        reason: 'no_initdata',
      });
      return {
        status: 'cached-session',
        session: { ...session, source: 'cached' },
        message: 'Используем сохранённую сессию',
      };
    }

    // No initData and no persisted session - require authentication
    clearSessionStore();
    console.warn('[Auth] Needs initData', {
      reason: 'no_initdata_no_session',
      hasUnsafeUserId: !!snapshot.unsafeUserId,
    });
    return {
      status: 'needs-initdata' as const,
      message:
        'Откройте мини-приложение через кнопку в Telegram, чтобы пройти авторизацию.',
    };
  }

  const meta = buildMetaPayload();

  console.log('[Auth] Calling /verify with:', {
    initDataLength: snapshot.raw.length,
    initDataPreview: snapshot.raw.substring(0, 100),
    hasMeta: !!meta,
  });

  try {
    const verifyResult = await api.verify(snapshot.raw, meta);
    
    console.log('[Auth] Verify result:', {
      success: verifyResult.success,
      hasSessionToken: !!(verifyResult as any).data?.sessionToken,
      hasUser: !!(verifyResult as any).data?.user,
      error: !verifyResult.success ? (verifyResult as any).error : null,
    });
    
    if (verifyResult.success) {
      const { userId: resolvedUserId, telegramUserId } = resolveUserIdentifiers(snapshot, verifyResult);
      if (typeof resolvedUserId !== 'number') {
        const state = buildNeedsInitDataState(
          'Не удалось определить пользователя Telegram. Откройте мини-приложение через кнопку в боте, чтобы обновить авторизацию.'
        );
        console.warn('[Auth] Success but no userId', {
          hasUnsafeUserId: !!snapshot.unsafeUserId,
          hasUserInResponse: !!(verifyResult.data.user?.id || verifyResult.data.user?.telegramId),
        });
        return state;
      }
      const sessionToken = verifyResult.data.sessionToken;

      const session: TelegramAuthSession = {
        userId: resolvedUserId,
        telegramUserId: telegramUserId ?? resolvedUserId,
        sessionToken,
        traceId: verifyResult.traceId,
        source: 'verified',
      };

      applySessionToStore(session);
      
      // Нормализуем expiresAt: сервер может вернуть число (timestamp) или строку (ISO)
      // Приводим к строке ISO для единообразия
      let expiresAtStr: string | undefined;
      if (verifyResult.data.expiresAt !== undefined) {
        if (typeof verifyResult.data.expiresAt === 'number') {
          expiresAtStr = new Date(verifyResult.data.expiresAt).toISOString();
        } else if (typeof verifyResult.data.expiresAt === 'string') {
          expiresAtStr = verifyResult.data.expiresAt;
        }
      }
      
      persistSession({
        userId: resolvedUserId,
        telegramUserId: session.telegramUserId,
        sessionToken,
        traceId: session.traceId,
        source: 'verified',
        storedAt: Date.now(),
        expiresAt: expiresAtStr,
      });

      const state = {
        status: 'authenticated' as const,
        session,
        message: 'Авторизация прошла успешно.',
      };
      
      // Structured logging for successful auth
      const webApp = runtimeWindow?.Telegram?.WebApp;
      console.log('[Auth] ✅ Authenticated', {
        userId: resolvedUserId,
        username: verifyResult.data.user?.username,
        language: verifyResult.data.user?.languageCode,
        platform: webApp?.platform,
        hasSessionToken: !!sessionToken,
      });
      
      return state;
    }

    const persisted = loadPersistedSession();
    if (persisted) {
      const session = buildSessionFromPersisted(persisted);
      applySessionToStore({ ...session, source: 'cached' });
      const state = {
        status: 'cached-session' as const,
        session: { ...session, source: 'cached' as const },
        message: 'Используем сохранённую сессию (проверка подписи не удалась).',
        error: (verifyResult as VerifyError).error.message,
      };
      console.warn('[Auth] Cached session (verify failed)', {
        userId: session.userId,
        errorCode: (verifyResult as VerifyError).error.code,
      });
      return state;
    }

    clearSessionStore();
    const state = mapVerificationErrorToState(verifyResult as VerifyError, snapshot);
    // Логирование уже добавлено в mapVerificationErrorToState
    return state;
  } catch (error) {
    const persisted = loadPersistedSession();
    if (persisted) {
      const session = buildSessionFromPersisted(persisted);
      applySessionToStore({ ...session, source: 'cached' });
      const state = {
        status: 'cached-session' as const,
        session: { ...session, source: 'cached' as const },
        message: 'Сеть недоступна, используем сохранённую сессию.',
        error: error instanceof Error ? error.message : 'Network error',
      };
      console.log('[Auth] Cached session (network error)', {
        userId: session.userId,
        errorType: error instanceof Error ? error.constructor.name : typeof error,
      });
      return state;
    }
    
    // Если нет сохранённой сессии, но есть userId из initDataUnsafe → read-only
    if (typeof snapshot.unsafeUserId === 'number') {
      const state = buildReadOnlyState(
        snapshot.unsafeUserId,
        'Сервер временно недоступен. Включён режим просмотра. Данные могут быть неактуальными.',
        error instanceof Error ? error.message : 'Network error'
      );
      console.warn('[Auth] Read-only fallback (exception, userId available)', {
        userId: snapshot.unsafeUserId,
        errorType: error instanceof Error ? error.constructor.name : typeof error,
      });
      return state;
    }

    // No persisted session and no userId → error
    clearPersistedSession();
    clearSessionStore();
    const state = {
      status: 'error' as const,
      error: error instanceof Error ? error.message : 'Network error',
      message:
        'Не удалось связаться с сервером. Попробуйте открыть мини-приложение снова.',
    };
    console.error('[Auth] ❌ Network error', {
      errorType: error instanceof Error ? error.constructor.name : typeof error,
      errorMessage: error instanceof Error ? error.message : 'Network error',
    });
    return state;
  }
}

export function getActiveSessionUserId(): number | null {
  return useSessionStore.getState().telegramUserId ?? null;
}

